import React, { useCallback, useEffect, useMemo } from "react";
import TableInfo from "../TableInfo/TableInfo";
import proposalsIcon from "../../../assets/proposals.svg";
import {
  getAmountOfProposalsForRealm,
  getTokensForAllTreasuries,
} from "../../../solana/common/helpers/user.dashboard.helpers";
import ProposalsItem from "../../ClubDetails/OverviewClub/Proposals/ProposalsItem/ProposalsItem";
import { generateSkeletonArrays } from "../../../utilities/helpers";
import {
  INITIAL_PROPOSAL_CARDS_COUNT,
  MESSAGE_TYPE,
} from "../../../common/constants/common.constants";
import ProposalsItemSkeleton from "../../ClubDetails/OverviewClub/Proposals/ProposalsItem/ProposalsItemSkeleton/ProposalsItemSkeleton";
import { createNotification } from "../../../utilities/notification";
import { useAnchorWallet } from "@solana/wallet-adapter-react";
import { proposalsStore } from "../../../contexts/proposalContext";
import { getProposalMetadataForContext } from "../../../solana/common/helpers/proposals.helpers";
import { IDashboardTokenAccountInfo } from "../../../common/interfaces/user-dashboard.interface";
import { dashboardStore } from "../../../contexts/dashboardStore";
import { IBasicProposalData } from "../../../common/interfaces/proposals.interface";

const ProposalsDashboard = () => {
  const {
    splProposals,
    clubs,
    treasuryData,
    setSplProposals,
    deposits,
    treasuryTokens,
    setTreasuryTokens,
  } = dashboardStore();

  const wallet = useAnchorWallet();

  useEffect(() => {
    if (!proposals || proposals.length === 0) void getAllSplProposals();
    if (!treasuryTokens || treasuryTokens.length === 0)
      void getAllTreasuryTokens();
    return () => {
      clearProposals();
      setSplProposals(undefined);
    };
  }, []);

  const { addNewProposal, updateProposal, clearProposals, proposals } =
    proposalsStore();

  const getAllSplProposals = useCallback(async () => {
    try {
      const allProposalsFromSpl = await getAmountOfProposalsForRealm(clubs!);
      setSplProposals(allProposalsFromSpl);
    } catch (error) {}
  }, [clubs, treasuryData]);

  const getAllTreasuryTokens = useCallback(async () => {
    try {
      if (treasuryData) {
        const loadedTreasuryTokens: IDashboardTokenAccountInfo[] =
          await getTokensForAllTreasuries(treasuryData);
        setTreasuryTokens(loadedTreasuryTokens);
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, "Problem loading treasury tokens");
    }
  }, [treasuryData]);

  const updateProposalData = async (proposal: IBasicProposalData) => {
    try {
      const relatedProposal = splProposals?.find(
        (p) =>
          p.proposalData.proposalAccount.pubkey.toString() ===
          proposal.proposalAccount.pubkey.toString()
      );
      const relatedClub = clubs!.find(
        (club) =>
          club.club.realmAddress === relatedProposal!.realmAddress.toString()
      );
      if (!relatedClub || !relatedProposal) {
        throw new Error();
      }
      const relatedTreasury = treasuryData!.find(
        (treasury) =>
          treasury?.club.realmAddress === relatedClub.club.realmAddress
      );
      if (!relatedTreasury) {
        throw new Error();
      }
      const relatedDeposit = deposits!.find(
        (dep) =>
          dep.treasury.toString() === relatedTreasury.treasuryAddress.toString()
      );
      const existingProposal = proposals!.find(
        (p) =>
          p.proposalBasicData.proposalAccount.pubkey.toString() ===
          proposal.proposalAccount.pubkey.toString()
      );
      const updatedProposalWithMetadta = await getProposalMetadataForContext(
        proposal,
        relatedClub.club.clubName,
        relatedClub.club.communityMint,
        {
          ownership: relatedTreasury?.ownership,
          depositRecords: relatedDeposit?.depositRecords ?? [],
          financialPower: relatedTreasury?.financialPower,
        },
        wallet!,
        relatedClub.club.type,
        relatedTreasury.denominatedTokenInfo,
        relatedTreasury.reservedRights,
        undefined
      );

      if (existingProposal) {
        updateProposal(updatedProposalWithMetadta);
      } else {
        addNewProposal(updatedProposalWithMetadta);
      }
    } catch (error) {
      console.log(error);
      createNotification(MESSAGE_TYPE.ERROR, "Problem loading proposal data.");
    }
  };

  const renderProposals = useMemo(() => {
    return splProposals && treasuryData && treasuryTokens
      ? splProposals.map((splProposal) => {
          const relatedClub = clubs!.find(
            (club) =>
              club.club.realmAddress === splProposal.realmAddress.toString()
          );

          const relatedTreasury = treasuryData!.find(
            (treasury) =>
              treasury &&
              treasury.club.realmAddress === splProposal.realmAddress.toString()
          );

          const relatedDeposit = deposits!.find(
            (dep) =>
              dep.treasury.toString() ===
              splProposal.proposalData.proposalMetadata.treasury.toString()
          );

          const relatedTokens = treasuryTokens.find(
            (item) =>
              item.treasuryAddress.toString() ===
              relatedTreasury?.treasuryAddress.toString()
          );
          if (relatedClub && relatedTreasury && relatedTokens) {
            return (
              <ProposalsItem
                splProposal={splProposal.proposalData}
                clubData={{
                  clubName: relatedClub.club.clubName,
                  clubType: relatedClub.club.type,
                  mint: relatedClub.club.communityMint,
                  roleConfig: relatedClub.club.roleConfig,
                  userRole: relatedClub.role,
                  isDashboardProposal: true,
                  fundraiseCount: relatedTreasury.fundraiseCount,
                  treasury: relatedTreasury.treasuryAddress,
                  address: relatedClub.club.clubAddress,
                  owner: relatedClub.clubAuthority.toString(),
                  withdrawalGovernance: relatedTreasury.withdrawalGovernance,
                  clubImageUrl: relatedClub.club.clubImageUrl,
                  denominatedTokenInfo: relatedTreasury.denominatedTokenInfo,
                  reservedRights: relatedTreasury.reservedRights,
                  memberRights: {
                    ownership: relatedTreasury.ownership,
                    depositRecords: relatedDeposit?.depositRecords ?? [],
                    financialPower: relatedTreasury?.financialPower,
                  },
                  treasuryTokens: relatedTokens.tokenAccounts,
                }}
                updateProposalWithMetadata={updateProposalData}
                key={splProposal.proposalData.proposalAccount.pubkey.toString()}
              />
            );
          } else {
            return <ProposalsItemSkeleton />;
          }
        })
      : generateSkeletonArrays(INITIAL_PROPOSAL_CARDS_COUNT).map((item) => {
          return <ProposalsItemSkeleton key={item} />;
        });
  }, [splProposals, clubs, treasuryData, treasuryTokens]);

  return (
    <div className="proposals-dashboard">
      <TableInfo
        title="Total active proposals"
        icon={proposalsIcon}
        description={`${splProposals?.length.toString()} proposals`}
      />
      {renderProposals}
    </div>
  );
};

export default ProposalsDashboard;
